import { toast } from "sonner";
import { downloadUrl } from "./api";

/**
 * Robust CSV download for sandboxed-iframe environments
 * (e.g. the Emergent preview iframe, Notion embeds, etc.) where
 * Chrome silently blocks programmatic anchor.click() downloads
 * because the parent iframe lacks the `allow-downloads` sandbox flag.
 *
 * Strategy:
 *   1. Fetch the CSV into a Blob.
 *   2. Try the standard `a.download` programmatic click — works in
 *      regular tabs and in iframes that DO allow downloads.
 *   3. Always also surface a long-lived Sonner toast with a real
 *      <a download href=…> link the user can physically click,
 *      plus a "Copy to clipboard" fallback that works in any context.
 *
 * @param {string} path - API path beginning with "/", e.g. "/orders/csv"
 * @param {object} params - query params; falsy values are dropped
 * @param {string} filename - the desired filename for the download
 */
export async function downloadCsv(path, params = {}, filename = "export.csv") {
  const url = downloadUrl(path, params);
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(msg || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);

  // First attempt: standard programmatic download.
  // If we're in a permissive context this saves immediately and the toast
  // below is just confirmation.
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    /* ignore — fallback is the toast link */
  }

  // Always surface a persistent toast with a real click-able link
  // (works even when programmatic clicks are sandboxed away).
  const sizeKb = (blob.size / 1024).toFixed(1);
  const tid = toast.success("CSV ready", {
    duration: 60000,
    description: (
      <div className="space-y-2 mt-1">
        <div className="text-xs text-zinc-500 font-mono-num">
          {filename} · {sizeKb} KB
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={objectUrl}
            download={filename}
            data-testid="csv-toast-download-link"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-medium text-zinc-900 no-underline"
            onClick={() => {
              setTimeout(() => toast.dismiss(tid), 600);
            }}
          >
            Save file
          </a>
          <button
            type="button"
            data-testid="csv-toast-copy-btn"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-medium text-zinc-900"
            onClick={async () => {
              try {
                const text = await blob.text();
                await navigator.clipboard.writeText(text);
                toast.success("CSV copied to clipboard", {
                  description: "Paste into Excel or Google Sheets",
                });
              } catch (e) {
                toast.error("Copy failed", { description: e.message });
              }
            }}
          >
            Copy
          </button>
        </div>
        <div className="text-2xs text-zinc-400">
          If "Save file" doesn't trigger a download in this preview, copy the
          CSV into Sheets/Excel — or open the app in a regular browser tab.
        </div>
      </div>
    ),
  });

  // Revoke the object URL after the toast expires, so the user has time to click.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  return filename;
}
